/**
 * A card component that contains board background, title, stats, links, etc.
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cx from 'classnames'
import MiniCard from '../MiniCard'
import MiniCardListItem from '../MiniCardListItem'
import BoardCoverImage from '../../../BoardCoverImage'
import { Title, Body, BtnLink } from '../MiniCardUIComponents'
import Link from 'client/shared/js/components/Link'
import UsernameTag from 'client/shared/js/components/UsernameTag'
import { convertTimestamp } from 'client/app/js/utils/date'
import { getAllAccessLevels } from 'client/app/js/utils/accessInterpreter/board'
import { getBoardRoute } from 'common/configs/routeMap'
import { selectors } from 'client/app/js/reducer'
import styles from './MiniBoardCard.module.scss'

const TOTAL_LIKES_THRESHOLD = 2
const propTypes = {
  className: PropTypes.string,
  board: PropTypes.object,
  onClick: PropTypes.func,
  fluid: PropTypes.bool,
  layout: PropTypes.oneOf(['card', 'list'])
}

const MiniBoardCard = ({ className, board, onClick, fluid, layout = 'card' }) => {
  const {
    title,
    owner,
    follows = {},
    meta = {},
    totalPlaces = 0
  } = board
  const totalLikes = follows.totalFollowers || 0
  const formattedUpdateDate = (meta.updatedDate) ? convertTimestamp(meta.updatedDate) : convertTimestamp(meta.createdDate)
  // const personalityType = owner.personalityType

  // Will determine the type of popularity text to show (i.e. total likes or total places)
  const popularityText = (totalLikes >= totalPlaces || totalLikes >= TOTAL_LIKES_THRESHOLD) ? `${totalLikes} likes` : `${totalPlaces} places`

  const user = useSelector(selectors.selectCurrentUser)
  const accessLevels = getAllAccessLevels(user, board)
  const url = getRoute(board, accessLevels)

  const handleVisitBoardLink = useCallback((evt) => {
    evt.stopPropagation()
  }, [])

  const VisitBoardLink = useMemo(() => (
    <Link to={url} onClick={handleVisitBoardLink}>
      <BtnLink>Visit board</BtnLink>
    </Link>
  ), [url, handleVisitBoardLink])

  const BoardDetails = useMemo(() => {
    return (
      <div className='mb-auto'>
        {layout === 'list' ? (
          <h3 className='font-small mb-1'>{title}</h3>
        ) : (
          <Title tagname='h4'>{title}</Title>
        )}
        <Body>
          <div className={cx(styles['createdby-label'])}>
            <UsernameTag username={owner.username} className='align-bottom' />
          </div>
          <span>{formattedUpdateDate} &#9679; {popularityText}</span>
        </Body>
      </div>
    )
  }, [title, owner, layout, formattedUpdateDate, popularityText])

  return layout === 'list' ? (
    <MiniCardListItem
      className={cx(styles['list-item'])}
      onClick={onClick}
      image={<BoardCoverImage board={board} />}
      imageClassName={cx(styles['list-img'])}>
      <div className={cx('d-flex flex-column justify-content-between h-100')}>
        {BoardDetails}
        {VisitBoardLink}
      </div>
    </MiniCardListItem>
  ) : (
    <MiniCard
      className={className}
      image={<BoardCoverImage board={board} />}
      footer={VisitBoardLink}
      onClick={onClick}
      fluid={fluid}>
      {BoardDetails}
    </MiniCard>
  )
}

MiniBoardCard.propTypes = propTypes

const getRoute = (board, { isOwner, isCollaborator }) => {
  const { bid, sharedBoardId } = board

  return (isOwner || isCollaborator)
    ? getBoardRoute({ boardId: bid })
    : getBoardRoute({ sharedBoardId })
}

export default MiniBoardCard
